import { useState } from "react"
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "@/contexts/AuthContext"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Separator } from "@/components/ui/separator"
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Video,
  FolderTree,
  UserCircle,
  Palette,
  LogOut,
  Menu,
  X,
  FileText,
  MessageSquare,
  Settings,
} from "lucide-react"
import { cn } from "@/lib/utils"
import logo from "@/logo.png"

const navigation = [
  {
    name: "Панель управления",
    href: "/",
    icon: LayoutDashboard,
  },
  {
    name: "Пользователи",
    href: "/users",
    icon: Users,
  },
  {
    name: "Категории",
    href: "/categories",
    icon: FolderTree,
  },
  {
    name: "Авторы",
    href: "/authors",
    icon: UserCircle,
  },
  {
    name: "Темы",
    href: "/themes",
    icon: Palette,
  },
  {
    name: "Курсы",
    href: "/courses",
    icon: BookOpen,
  },
  {
    name: "Видео",
    href: "/videos",
    icon: Video,
  },
  {
    name: "Блоги",
    href: "/blogs",
    icon: FileText,
  },
  {
    name: "Отзывы",
    href: "/reviews",
    icon: MessageSquare,
  },
  {
    name: "Настройки сайта",
    href: "/settings",
    icon: Settings,
  },
]

export default function AdminLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate("/login")
  }

  // const getUserInitials = () => {
  //   if (!user) return "?"
  //   const names = user.full_name.split(" ")
  //   if (names.length >= 2) {
  //     return `${names[0][0]}${names[1][0]}`.toUpperCase()
  //   }
  //   return user.full_name.substring(0, 2).toUpperCase()
  // }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 transition-transform duration-300 ease-in-out lg:translate-x-0",
          "bg-[#0B0B0D] border-r border-white/5",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-center h-20 px-6 border-b border-white/10">
          <img 
            src={logo} 
            alt="Skylls Logo" 
            className="h-12 w-auto object-contain"
          />
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden absolute right-4 text-white hover:bg-white/10"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            )
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-full px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-10 w-10 rounded-full"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.avatar || undefined} />
                    <AvatarFallback>Dev</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.full_name}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || user?.phone}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Выйти</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
